import axios from "axios";

import { fileServiceEndpoint } from "api/files";

import { verifyDownloadChecksum } from "../../../utils";
import { useFilesContext, useFilesDispatch } from "../context";

export function useDownloadFile() {
  const { filesType } = useFilesContext();
  const dispatch = useFilesDispatch();

  const downloadFile = (filename: string, fileSize: number) => {
    const abortController = new AbortController();

    const downloadChunks = async () => {
      try {
        const CHUNK_SIZE = 1024 * 1024 * 4;
        const fileData = [];
        let downloadedChunks = 0;
        const totalChunks = Math.ceil(fileSize / CHUNK_SIZE);

        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
          if (abortController.signal.aborted) {
            break;
          }

          const response = await axios.get(
            `${fileServiceEndpoint}/files/${filesType}/${filename}`,
            {
              params: { chunkIndex },
              responseType: "blob",
              signal: abortController.signal
            }
          );

          fileData.push(response.data);
          downloadedChunks += 1;
          const progress = Math.round((downloadedChunks / totalChunks) * 100);
          dispatch({ payload: { downloadFileProgress: progress } });
        }

        if (!abortController.signal.aborted) {
          // Combine all the chunks into a single Blob
          const completeFile = new Blob(fileData);

          // Get the overall checksum from the first response headers (if available)
          // Note: This assumes the server provides an overall file checksum
          const firstResponse = await axios.head(
            `${fileServiceEndpoint}/files/${filesType}/${filename}`
          );
          const checksumHeader = firstResponse.headers["x-checksum"];

          if (checksumHeader) {
            try {
              await verifyDownloadChecksum(completeFile, checksumHeader);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error("Checksum verification failed:", error);
              throw error;
            }
          }

          const url = window.URL.createObjectURL(completeFile);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          // eslint-disable-next-line no-console
          console.error("Download error:", error);
        }
      }
    };

    const cancelDownload = () => {
      abortController.abort();
    };
    return {
      downloadChunks,
      cancelDownload
    };
  };

  return { downloadFile };
}
